import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { LoadingState, loadingSelector } from "../../../common/state/slice/appStateSlice";
import { Dropdown, Input } from "src/common/components/Input";
import TextArea from "src/common/components/Input/TextArea";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import InformationFrame from "src/common/components/InfomationFrame/InformationFrame";
import Spinner from "src/common/components/Loader/Spinner";
import { useAppDispatch } from "src/common/state/hooks";
import { getCurrentUser, updateUser } from "src/features/profile/state/profileSlice";
import { getCurrentModalStateSlider } from "src/common/state/slice/modal/modalSlice";
import {
  generateVoiceOver,
  getElevenlabsVoices,
  getPreferredElevenLabsVoice,
  getTtsPreview,
  mediaSelector,
  resetTtsPreview,
  uploadMultipart,
} from "../state/gallerySlice";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { RootState } from "src/common/state/store";
import { fromFileReturnMD5AndSize } from "../state/uploadUtil";
import { MediaGenerationInfoParagraph } from "./MediaGenerationInfoParagraph";
import { GENERATE_MEDIA_PANEL_COMPONENT } from "./GenerateMediaPanel";
import { IMedia } from "src/model/unityObject";
import RangeSelector from "../../../common/components/RangeSelector/RangeSelector";
import Tooltip from "@mui/material/Tooltip";
import { InformationCircleIcon } from "@heroicons/react/outline";

type Props = {
  closeSliderAnimation(): any;
};

const MAX_CHAR_NB = 1500;

const LabelWithTooltip = ({
  label,
  tooltip,
  value,
}: {
  label: string;
  tooltip: string;
  value?: string;
}) => {
  return (
    <div className="flex items-center justify-center gap-2 mb-2">
      <Tooltip title={tooltip} arrow placement="top">
        <div className="w-4 h-4 text-gray-500 cursor-help flex-shrink-0">
          <InformationCircleIcon className="w-full h-full" />
        </div>
      </Tooltip>
      <label className="text-center text-sm font-medium text-black dark:text-black">
        {label}
        {value && ` : ${value}`}
      </label>
    </div>
  );
};

export const GenerateTtsPanel = ({ closeSliderAnimation }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const currentUser = useSelector(getCurrentUser);
  const openSliderId = useSelector(getCurrentModalStateSlider);
  const ttsPreview = useSelector(getTtsPreview);
  const loading = useSelector((state: RootState) =>
    loadingSelector(state, GENERATE_MEDIA_PANEL_COMPONENT),
  );

  const [isFormEnabled, setIsFormEnabled] = useState(true);
  const [isTtsPreviewPreparingToBeDisplayed, setIsPreviewPreparingToBeDisplayed] = useState(false);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [mediaNameToEdit, setMediaNameToEdit] = useState<string>("");
  const [nameUnique, setNameUnique] = useState(true);
  const medias = useSelector(mediaSelector);

  const elevenLabsVoices = useSelector(getElevenlabsVoices);
  const mapElevenlabsVoices = useMemo(
    () => () =>
      elevenLabsVoices.map((voice) => {
        const localizedVoiceGender = t(`general.${voice.gender}`);
        return { ...voice, value: voice.id, optionText: `${voice.name} (${localizedVoiceGender})` };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [elevenLabsVoices, t],
  );

  const defaultTtsVoice = useSelector(getPreferredElevenLabsVoice);
  const [voice, setVoice] = useState(defaultTtsVoice);
  const [voiceOptions, setVoiceOptions] = useState(mapElevenlabsVoices());
  const [voiceSettings, setVoiceSettings] = useState({
    stability: 0.3,
    similarity_boost: 0.3,
    style: 0.2,
    use_speaker_boost: true,
    speed: 1.0,
  });

  // Form
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
    watch,
  } = useForm<{ textToGenerate: string }>({
    defaultValues: { textToGenerate: "" },
  });

  const textValue = watch("textToGenerate") || "";
  const textLen = textValue.length;

  const getErrorText = () => {
    switch (errors.textToGenerate?.type) {
      case "maxLength":
        return t("pages.gallery.generateMediaPanel.aboveMaxCharacters");
      case "isNotEmpty":
        return t("pages.gallery.generateVoicePanel.emptyText");
      default:
        return "";
    }
  };

  const resetTts = () => {
    setIsFormEnabled(true);
    reset({ textToGenerate: "" });
    setMediaNameToEdit("");
    dispatch(resetTtsPreview());
    setVoice(defaultTtsVoice);
    setVoiceOptions(mapElevenlabsVoices());
  };

  const tts = async () => {
    const nameNotAvailable = medias.find((m: IMedia) => m.name === mediaNameToEdit);

    if (nameNotAvailable) {
      setNameUnique(false);
      return;
    } else {
      setNameUnique(true);
    }

    setIsFormEnabled(false);
    dispatch(
      updateUser({
        ...currentUser,
        preferences: JSON.stringify({
          favoriteVoiceId: voice.voiceId,
          favoriteVoiceTTSProvider: voice.ttsProvider,
        }),
      }),
    );

    setIsPreviewPreparingToBeDisplayed(true);

    await dispatch(
      generateVoiceOver({
        voice: voice.voiceId,
        ttsProvider: voice.ttsProvider,
        text: getValues("textToGenerate"),
        componentId: GENERATE_MEDIA_PANEL_COMPONENT,
        language: elevenLabsVoices.find((elt) => elt.id === voice.voiceId)?.language,
        voiceSettings: voice.ttsProvider === "elevenLabs" ? voiceSettings : undefined,
      }),
    ).then((res: any) => {
      setIsAlertVisible(true);
      return res.payload;
    });
  };

  useEffect(() => {
    resetTts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSliderId]);

  useEffect(() => {
    if (ttsPreview) {
      setIsPreviewPreparingToBeDisplayed(false);
      const textToGenerate = getValues("textToGenerate");
      const sanitizedText = textToGenerate.replace(/\s+/g, " ").trim();
      const defaultName = sanitizedText.slice(0, 29).trimEnd();
      setMediaNameToEdit(defaultName);

      setTimeout(() => {
        const inputElement = document.querySelector("input[name='name']") as HTMLInputElement;
        if (inputElement) inputElement.select();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttsPreview]);

  return (
    <div>
      {isAlertVisible && (
        <InformationFrame
          className="w-[90%] px-6 py-6 mx-auto absolute top-[-20px] transition-opacity z-[1000]"
          style="success"
          onClose={() => setIsAlertVisible(false)}
          withTimeout
          showCloseButton
        >
          <div>{t("pages.gallery.generateVoicePanel.success")}</div>
        </InformationFrame>
      )}

      <h3 className="font-medium text-lg mb-6 mt-2">
        {t("pages.gallery.generateVoicePanel.title")}
      </h3>

      <form onSubmit={handleSubmit(tts)}>
        {ttsPreview && (
          <>
            <div className="block font-medium text-gray-700 ">{t("general.fileName")}</div>
            <Input
              className="mb-4"
              name="name"
              type="text"
              maxLength={30}
              value={mediaNameToEdit}
              placeholder={t("general.fileName")}
              onChange={(e) => setMediaNameToEdit(e.target.value)}
              required
              disabled={loading === LoadingState.Loading}
            />
            <div className={`text-red-600 ${nameUnique ? "invisible" : ""}`}>
              {t("general.mediaNameAlreadyUsed")}
            </div>
          </>
        )}

        {!ttsPreview && (
          <>
            <MediaGenerationInfoParagraph text={t("pages.gallery.generateVoicePanel.disclaimer")} />

            {/* <MediaGenerationInfoParagraph
              text={`${t("pages.gallery.generateVoicePanel.disclaimer")}
              ${t("pages.gallery.generateVoicePanel.disclaimer2")}`}
            /> */}

            <Dropdown
              options={voiceOptions}
              label={t("pages.gallery.generateVoicePanel.voice")}
              labelFontClassName="text-md mt-2"
              onChangeCb={(_value: string, index: number) => {
                setVoice({
                  voiceId: voiceOptions[index].id,
                  ttsProvider: voiceOptions[index].tts_provider,
                });
              }}
              asDefaultValue
              defaultValueIndex={voiceOptions.findIndex((o) => o.value === voice.voiceId) || 0}
            />

            {voice.ttsProvider === "elevenLabs" && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium mb-4 text-gray-700 text-center">
                  {t("pages.gallery.generateVoicePanel.elevenLabsSettings")}
                </h4>

                <div className="space-y-4">
                  <div>
                    <LabelWithTooltip
                      label={t("pages.gallery.generateVoicePanel.stability")}
                      tooltip={t("pages.gallery.generateVoicePanel.stabilityTooltip")}
                      value={`${Math.round(voiceSettings.stability * 100)}%`}
                    />
                    <RangeSelector
                      value={voiceSettings.stability}
                      setValue={(val: number) =>
                        setVoiceSettings({ ...voiceSettings, stability: val })
                      }
                      disabled={false}
                      className="w-full h-2 bg-gray-200 rounded-lg cursor-pointer"
                      printValue={false}
                    />
                  </div>

                  <div>
                    <LabelWithTooltip
                      label={t("pages.gallery.generateVoicePanel.similarityBoost")}
                      tooltip={t("pages.gallery.generateVoicePanel.similarityBoostTooltip")}
                      value={`${Math.round(voiceSettings.similarity_boost * 100)}%`}
                    />
                    <RangeSelector
                      value={voiceSettings.similarity_boost}
                      setValue={(val: number) =>
                        setVoiceSettings({ ...voiceSettings, similarity_boost: val })
                      }
                      disabled={false}
                      className="w-full h-2 bg-gray-200 rounded-lg cursor-pointer"
                      printValue={false}
                    />
                  </div>

                  <div>
                    <LabelWithTooltip
                      label={t("pages.gallery.generateVoicePanel.style")}
                      tooltip={t("pages.gallery.generateVoicePanel.styleTooltip")}
                      value={`${Math.round(voiceSettings.style * 100)}%`}
                    />
                    <RangeSelector
                      value={voiceSettings.style}
                      setValue={(val: number) => setVoiceSettings({ ...voiceSettings, style: val })}
                      disabled={false}
                      className="w-full h-2 bg-gray-200 rounded-lg cursor-pointer"
                      printValue={false}
                    />
                  </div>

                  <div>
                    <LabelWithTooltip
                      label={t("pages.gallery.generateVoicePanel.speed")}
                      tooltip={t("pages.gallery.generateVoicePanel.speedTooltip")}
                      value={`${voiceSettings.speed.toFixed(1)}x`}
                    />
                    <RangeSelector
                      value={voiceSettings.speed}
                      setValue={(val: number) => setVoiceSettings({ ...voiceSettings, speed: val })}
                      min={0.7}
                      max={1.2}
                      step={0.1}
                      valueMultiplier={1}
                      valueSuffix="x"
                      disabled={false}
                      className="w-full h-2 bg-gray-200 rounded-lg cursor-pointer"
                      decimalPlaces={1}
                      printValue={false}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <div className={!ttsPreview ? "min-h-[20rem]" : undefined}>
          <IsLoading
            componentId={GENERATE_MEDIA_PANEL_COMPONENT}
            spinnerPlaceholder
            showSuccess={false}
            // Only reserve spinner height when not in preview mode
            spinnerStyle={!ttsPreview ? { minHeight: "20rem" } : undefined}
          >
            {!ttsPreview ? (
              isTtsPreviewPreparingToBeDisplayed ? (
                <div className="flex items-center justify-center relative h-full">
                  <Spinner />
                </div>
              ) : (
                <>
                  <div className="block font-medium text-gray-700 mt-4">
                    {t("general.textVoice")}
                  </div>
                  <TextArea
                    isResizable={false}
                    className="bg-white w-full h-64 mt-4"
                    placeholder={t("pages.gallery.generateVoicePanel.textInputPlaceholder")}
                    maxLength={MAX_CHAR_NB}
                    registerFct={() =>
                      register("textToGenerate", {
                        maxLength: MAX_CHAR_NB,
                        validate: { isNotEmpty: (value) => value.trim().length > 0 },
                      })
                    }
                  />

                  <div
                    style={{
                      color: textLen >= MAX_CHAR_NB ? "red" : "gray",
                      marginTop: "0.5rem",
                      fontSize: "0.875rem",
                    }}
                  >
                    {textLen}/{MAX_CHAR_NB}
                  </div>

                  {errors.textToGenerate && (
                    <span className="text-red-600 text-sm mt-2">{getErrorText()}</span>
                  )}
                </>
              )
            ) : (
              <div className="mt-4">
                <audio id="tts-preview" className="w-full" controls autoPlay>
                  <source src={ttsPreview.url} type="audio/mpeg" />
                </audio>
              </div>
            )}

            <div className="mt-5" key={JSON.stringify(ttsPreview)}>
              {!ttsPreview ? (
                <button
                  disabled={!isFormEnabled}
                  className={`btn-primary-fill mr-3 mt-2 ${
                    !isFormEnabled ? "cursor-not-allowed" : ""
                  }`}
                  type="submit"
                >
                  {t("general.generate")}
                </button>
              ) : (
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    className="btn-primary-outline mr-3 mt-2"
                    onClick={resetTts}
                  >
                    {t("general.cancel")}
                  </button>

                  <button
                    type="button"
                    className="btn-primary-fill mr-3 mt-2"
                    disabled={!mediaNameToEdit}
                    onClick={async () => {
                      if (!ttsPreview) return;
                      try {
                        const nameNotAvailable = medias.find(
                          (m: IMedia) => m.name === mediaNameToEdit,
                        );

                        if (nameNotAvailable) {
                          setNameUnique(false);
                          return;
                        } else {
                          setNameUnique(true);
                        }

                        const response = await fetch(ttsPreview.url);
                        if (!response.ok) throw new Error(`Blob fetch failed: ${response.status}`);
                        const blob = await response.blob();
                        const file = new File([blob], `${mediaNameToEdit || "audio_preview"}.mp3`, {
                          type: "audio/mpeg",
                        });

                        const { MD5 } = await fromFileReturnMD5AndSize(file);

                        const uploadParams = {
                          file,
                          orgId: currentUser.organization.id,
                          name: mediaNameToEdit || "audio_preview",
                          media_360_tag: "other",
                          MD5,
                          metadata: { initialPrompt: getValues("textToGenerate") },
                        };

                        dispatch(uploadMultipart(uploadParams))
                          .then(() => {
                            dispatch(
                              setAlert({
                                type: "success",
                                msg: t("pages.gallery.generateVoicePanel.uploadSuccess"),
                              }),
                            );
                            resetTts();
                            closeSliderAnimation();
                          })
                          .catch((err) => {
                            console.error("Upload error:", err);
                          });
                      } catch (err) {
                        console.error("Error handling file upload:", err);
                        dispatch(
                          setAlert({
                            type: "error",
                            msg:
                              t("pages.gallery.generateVoicePanel.uploadFailure") ??
                              "Upload failed.",
                          }),
                        );
                      }
                    }}
                  >
                    {t("general.save")}
                  </button>
                </div>
              )}
            </div>
          </IsLoading>
        </div>
      </form>
      <MediaGenerationInfoParagraph
        text={t("pages.gallery.generateVoicePanel.disclaimer2")}
        customStyle={{ marginTop: "1.5rem" }}
      />
    </div>
  );
};

export default GenerateTtsPanel;
